
import { Accordion, Box, BoxProps, Title, List } from "@mantine/core";
import classes from "./Faqs.module.scss";

const FaqData = [
  {
    id: "what-is",
    question: "What does RunAnalytic do and how does it comply with Amazon's SP-API policies?",
    answer: (
      <span className="text-base">
        RunAnalytic is a secure, cloud-based SaaS platform that connects with Amazon Seller accounts through the Selling Partner API (SP-API) to generate and upload tax-compliant invoices directly to Amazon. We access only the minimum order data required, never use customer information for any other purpose, encrypt all data in transit and at rest, and automatically delete Amazon customer data (PII) within 30 days in accordance with{' '}
        <a
          href="https://sellercentral.amazon.es/mws/static/policy?documentType=DPP&locale=en_EN"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline text-blue-600"
        >
          Amazon's Data Protection Policy
        </a>.
      </span>
    )
  },
  {
    id: "benefit",
    question: "Who can benefit from using RunAnalytic?",
    answer: (
      <span className="text-base">
        RunAnalytic helps Amazon sellers in the UAE and other supported regions automatically generate and upload tax-compliant invoices for every order, ensuring full compliance with Amazon's invoicing requirements.
      </span>
    )
  },
  {
    id: "compliance",
    question: "Does RunAnalytic comply with Amazon's SP-API guidelines and data protection policies?",
    answer: (
      <span className="text-base">
        <span className="block leading-relaxed py-3">Yes. RunAnalytic strictly follows Amazon's SP-API guidelines and Data Protection Policy. Our compliance measures include:</span>
        <List spacing={12} withPadding listStyleType="disc" className="font-weight-500">
          <List.Item><span className="underline font-semibold">Encryption:</span> All Amazon customer data (PII) is encrypted in transit (TLS 1.2/1.3) and at rest (AES-256).</List.Item>
          <List.Item><span className="underline font-semibold">Access Controls:</span> Data access is restricted to authorized automated processes. Administrative accounts are protected by strong password policies and Multi-Factor Authentication (MFA).</List.Item>
          <List.Item><span className="underline font-semibold">Minimal Data Retention:</span> We only use Amazon customer data (PII) for invoice generation. All Amazon customer data (PII) is automatically deleted within 30 days, in accordance with Amazon's Data Protection Policy (DDP).</List.Item>
          <List.Item><span className="underline font-semibold">Logging Practices:</span> Amazon customer data (PII) is never stored in application logs. Logs contain only non-sensitive technical metadata.</List.Item>
          <List.Item><span className="underline font-semibold">Purpose Limitation:</span> Data retrieved via SP-API is used exclusively to generate and upload tax-compliant invoices.</List.Item>
        </List>
      </span>
    )
  },
  {
    id: "data-access",
    question: "What Amazon customer data (PII) does RunAnalytic access?",
    answer: (
      <span className="text-base">
        We only access the information required to create invoices: order ID, customer name, shipping address, and tax details. No other customer data is collected or used. This data is retrieved via Amazon's SP-API only, not stored permanently, and auto-deleted after use.
      </span>
    )
  },
  {
    id: "data-protection",
    question: "How does RunAnalytic protect Amazon customer data (PII)?",
    answer: (
      <span className="text-base">
        All Amazon customer data (PII) is encrypted in transit (TLS 1.2/1.3) and at rest (AES-256). Our systems are hosted on secure cloud infrastructure with firewalls, intrusion detection, and strict access controls. Access is logged and monitored continuously for suspicious activity.
      </span>
    )
  },
  {
    id: "data-storage",
    question: "Does RunAnalytic store or log Amazon customer data (PII)?",
    answer: (
      <span className="text-base">
        No. We do not permanently store Amazon customer data (PII). All retrieved data is automatically deleted within 30 days. Logs only capture technical metadata (timestamps, error codes, request IDs) and never contain Amazon customer data (PII).
      </span>
    )
  },
  {
    id: "marketplaces",
    question: "Which Amazon marketplaces does RunAnalytic currently support?",
    answer: (
      <span className="text-base">
        Currently, RunAnalytic supports Amazon UAE(amazon.ae) sellers through SP-API integration. We are actively expanding to support additional Amazon marketplaces, including KSA, UK, and USA.
      </span>
    )
  },
  {
    id: "data-access-control",
    question: "Who can access Amazon customer data (PII) within RunAnalytic?",
    answer: (
      <span className="text-base">
        Amazon customer data (PII) is processed automatically by our systems. RunAnalytic staff do not have direct access to Amazon customer data (PII), except if strictly required for troubleshooting under a confidentiality agreement. Administrative access is protected by strong passwords and Multi-Factor Authentication (MFA).
      </span>
    )
  },
  {
    id: "password-rules",
    question: "What password and authentication rules do you enforce?",
    answer: (
      <span className="text-base">
        <List spacing={12} withPadding listStyleType="disc" className="text-md font-weight-500 pt-3">
          <List.Item>Passwords must be at least 12 characters long with uppercase, lowercase, numbers, and special characters.</List.Item>
          <List.Item>3 failed attempts allowed before a temporary lock-out.</List.Item>
          <List.Item>1 day of minimum password age.</List.Item>
          <List.Item>90 days of password expiry time.</List.Item>
          <List.Item>Multi-Factor Authentication (MFA) is enforced for all administrative accounts.</List.Item>
        </List>
      </span>
    )
  },
  {
    id: "data-breach",
    question: "What happens if there is a data breach?",
    answer: (
      <span className="text-base">
        In the unlikely event of a data breach affecting Amazon customer data (PII), we will immediately secure affected systems, notify Amazon's security team via email (security@amazon.com) and impacted parties within 24 hours, and carry out a full investigation in line with industry best practices and legal requirements.
      </span>
    )
  },
  {
    id: "policies",
    question: "Which Amazon policies do you comply with?",
    answer: (
      <span className="leading-relaxed text-base">
        <List spacing={12} withPadding listStyleType="disc" className="text-md font-weight-500 pt-3">
          <List.Item>Amazon's SP-API <a href="https://sellercentral.amazon.es/mws/static/policy?documentType=AUP&locale=en_EN" target="_blank" className="hover:underline text-blue-600">Acceptable Use Policy</a>.</List.Item>
          <List.Item>Amazon's <a href="https://sellercentral.amazon.es/mws/static/policy?documentType=DPP&locale=en_EN" target="_blank" className="hover:underline text-blue-600">Data Protection Policy</a> (DPP).</List.Item>
          <List.Item><a href="https://developer-docs.amazon.com/sp-api/docs/policies-and-agreements?utm_source=chatgpt.com" target="_blank" className="hover:underline text-blue-600">Amazon Services API Developer Agreement</a> for UAE region</List.Item>
          <List.Item>International best practices such as NIST/ISO27001 security standards.</List.Item>
        </List>
      </span>
    )
  },
  {
    id: "support",
    question: "How can I get support from RunAnalytic?",
    answer: (
      <span className="text-base">
        You can reach our support team via email within standard support hours at {" "}
        <a
          href="https://mail.google.com/mail/?view=cm&fs=1&to=support@runanalytic.com"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline text-blue-600"
        >
          support@runanalytic.com
        </a>
        . We provide assistance for setup, troubleshooting, and compliance-related questions. Additional resources, including documentation and FAQs, are also available on our website.
      </span>
    )
  },
  {
    id: "free-trial",
    question: "Does RunAnalytic offer a free trial?",
    answer: (
      <span className="text-base">
        Yes! We offer a limited-time free trial so you can explore all features of our platform before making a commitment. This allows Amazon sellers to test our invoice automation, compliance features, and SP-API integration with no upfront cost. Even during free trials, all Amazon SP-API data handling follows <a href="https://sellercentral.amazon.es/mws/static/policy?documentType=DPP&locale=en_EN" target="_blank" className="hover:underline text-blue-600">Amazon's Data Protection Policy</a>.
      </span>
    )
  }
];

type FaqsProps = BoxProps;

const Faqs = ({ ...others }: FaqsProps) => {
  return (
    <Box {...others}>
      <Title
        order={3}
        my='md'
        ta='center'
      >
        Frequently Asked Questions
      </Title>

      <Accordion
        radius='md'
        classNames={{
          root: classes.root,
          item: classes.item,
          panel: classes.panel,
          chevron: classes.chevron,
        }}
      >
        {FaqData.map((faq) => (
          <Accordion.Item key={faq.id} value={faq.id}>
            <Accordion.Control>{faq.question}</Accordion.Control>
            <Accordion.Panel>{faq.answer}</Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Box>
  );
};

export default Faqs;